// PRE RMA CSB — Enveloppe fine autour de la reconnaissance vocale du NAVIGATEUR (Web Speech API).
//
// ⚠ Confidentialité / fonctionnement réel (à lire avant de dire que « ça marche hors ligne ») :
//   - Chrome (ordinateur et Android) envoie l'audio au service de reconnaissance de Google ; PRE RMA ne l'enregistre jamais
//     (aucun MediaRecorder, aucun fichier audio) mais n'a AUCUN contrôle sur ce service. Internet est donc nécessaire : non vérifié hors connexion.
//   - Firefox ne propose pas cette API ; Safari/iOS : comportement non vérifié. Sans API, l'écran propose la saisie au clavier.
//   - Le français (fr-FR) est pris en charge par ce service ; le malgache (mg-MG) est EXPÉRIMENTAL : sa disponibilité dépend du navigateur
//     et de l'appareil et n'a pas pu être vérifiée ici.
// Ce module ne contient aucune règle métier : il transmet des textes (finaux) à l'écran, qui les passe à shared/business-logic/saisieVocale.ts.

export type ErreurVocale = 'non-supporte' | 'permission' | 'reseau' | 'micro' | 'langue' | 'inconnue';

interface ResultatBrut { isFinal: boolean; 0: { transcript: string; confidence: number }; length: number }
interface EvenementBrut { resultIndex: number; results: ArrayLike<ResultatBrut> }
interface ReconnaissanceBrute {
  lang: string; continuous: boolean; interimResults: boolean; maxAlternatives: number;
  onresult: ((e: EvenementBrut) => void) | null; onerror: ((e: { error: string }) => void) | null; onend: (() => void) | null; onstart: (() => void) | null;
  start(): void; stop(): void; abort(): void;
}
type FabriqueReconnaissance = new () => ReconnaissanceBrute;

function fabrique(): FabriqueReconnaissance | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: FabriqueReconnaissance; webkitSpeechRecognition?: FabriqueReconnaissance };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Le navigateur propose-t-il la dictée ? (Ne dit rien de la disponibilité du service ni de la langue.) */
export const dicteeDisponible = (): boolean => fabrique() !== null;
export const contexteSecurise = (): boolean => typeof window !== 'undefined' && (window.isSecureContext || location.hostname === 'localhost');

export interface OptionsMoteur {
  langue: string;
  /** true : écoute continue (ordinateur). false : une phrase par session, relancée automatiquement (souvent plus fiable sur Android). Défaut : true. */
  continu?: boolean;
  onFinal: (texte: string, confiance: number | undefined) => void;
  onInterim: (texte: string) => void;
  onEcoute: (actif: boolean) => void;
  onErreur: (code: ErreurVocale, detail: string) => void;
}
export interface MoteurVocal { demarrer: () => void; arreter: () => void; actif: () => boolean }

const ERREURS_FATALES: Record<string, ErreurVocale> = {
  'not-allowed': 'permission', 'service-not-allowed': 'permission', network: 'reseau', 'audio-capture': 'micro', 'language-not-supported': 'langue',
};
const MESSAGES: Record<ErreurVocale, string> = {
  'non-supporte': 'La dictée vocale n’est pas disponible sur ce navigateur.',
  permission: 'Accès au microphone refusé pour ce site. Touchez le cadenas à gauche de l’adresse > Autorisations > Micro > Autoriser (ou Chrome > Paramètres > Paramètres des sites > Micro), puis réessayez.',
  reseau: 'Le service de reconnaissance vocale est injoignable (Internet requis).',
  micro: 'Aucun microphone utilisable (ou il est déjà utilisé par une autre application : fermez-la et réessayez).',
  langue: 'Cette langue n’est pas prise en charge par la reconnaissance vocale de ce navigateur.',
  inconnue: 'La reconnaissance vocale s’est arrêtée de façon inattendue.',
};
export const messageErreurVocale = (c: ErreurVocale) => MESSAGES[c];

export type ResultatMicro = { ok: true } | { ok: false; code: ErreurVocale; detail: string };
/**
 * Comme la recherche vocale de Google : on demande d'abord l'autorisation du micro (getUserMedia), DANS le geste de l'utilisateur, ce qui
 * fait apparaître la demande d'autorisation de façon fiable sur Android et donne la VRAIE cause en cas d'échec (refusé, micro absent ou occupé).
 * Aucun enregistrement : le flux est coupé aussitôt, avant de lancer la reconnaissance (sinon le micro serait « occupé » pour elle).
 * Si l'API est absente ou l'erreur inconnue, on laisse la reconnaissance décider (ok: true).
 */
export async function preparerMicro(): Promise<ResultatMicro> {
  const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
  if (!md?.getUserMedia) return { ok: true };
  try {
    const flux = await md.getUserMedia({ audio: true });
    flux.getTracks().forEach((t) => t.stop());
    await new Promise((r) => setTimeout(r, 200)); // laisse Android libérer le micro avant la reconnaissance
    return { ok: true };
  } catch (e) {
    const nom = (e as { name?: string })?.name ?? '';
    if (nom === 'NotAllowedError' || nom === 'SecurityError' || nom === 'PermissionDeniedError') return { ok: false, code: 'permission', detail: nom };
    if (nom === 'NotFoundError' || nom === 'DevicesNotFoundError') return { ok: false, code: 'micro', detail: nom };
    if (nom === 'NotReadableError' || nom === 'TrackStartError' || nom === 'AbortError') return { ok: false, code: 'micro', detail: 'micro-occupe' };
    return { ok: true };
  }
}

export function creerMoteur(o: OptionsMoteur): MoteurVocal {
  const Ctor = fabrique();
  let rec: ReconnaissanceBrute | null = null;
  let voulu = false;
  let tournant = false;
  let relances: number[] = [];

  function construire(): ReconnaissanceBrute {
    const r = new Ctor!();
    r.lang = o.langue; r.continuous = o.continu ?? true; r.interimResults = true; r.maxAlternatives = 1;
    r.onstart = () => { tournant = true; o.onEcoute(true); };
    r.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        const texte = (res[0]?.transcript ?? '').trim();
        if (res.isFinal) { if (texte) o.onFinal(texte, res[0]?.confidence); } else interim += `${texte} `;
      }
      o.onInterim(interim.trim());
    };
    r.onerror = (e) => {
      const fatal = ERREURS_FATALES[e.error];
      if (fatal) { voulu = false; o.onErreur(fatal, e.error); return; }
      // « no-speech » (silence) et « aborted » : normaux, on laisse onend relancer. Un silence n'est JAMAIS une validation.
      if (e.error !== 'no-speech' && e.error !== 'aborted') { voulu = false; o.onErreur('inconnue', e.error); }
    };
    r.onend = () => {
      tournant = false; o.onEcoute(false); o.onInterim('');
      if (!voulu) return;
      // Chrome coupe la session après quelques secondes de silence : on relance, sauf boucle d'échecs (> 5 relances en 3 s).
      const t = Date.now();
      relances = relances.filter((x) => t - x < 3000); relances.push(t);
      if (relances.length > 5) { voulu = false; o.onErreur('inconnue', 'relances-rapides'); return; }
      setTimeout(() => { if (voulu && !tournant) try { rec?.start(); } catch { /* déjà démarré */ } }, 250);
    };
    return r;
  }

  return {
    demarrer() {
      if (!Ctor) { o.onErreur('non-supporte', 'api-absente'); return; }
      voulu = true; relances = [];
      if (!rec) rec = construire();
      if (tournant) return;
      try { rec.start(); } catch { /* start() lève si déjà démarré : sans conséquence */ }
    },
    arreter() {
      voulu = false;
      try { rec?.stop(); } catch { /* déjà arrêté */ }
      o.onInterim('');
    },
    actif: () => voulu,
  };
}

// ---- Lecture à voix haute (« répéter la dernière valeur ») -----------------------------------------------------------
export const lectureDisponible = (): boolean => typeof window !== 'undefined' && 'speechSynthesis' in window;
/** Lit un texte court (jamais le contenu complet d'une ligne par défaut). `fini` est toujours appelé, même en cas d'erreur. */
export function lireAVoixHaute(texte: string, langue: string, fini: () => void): void {
  if (!lectureDisponible()) { fini(); return; }
  try {
    const u = new SpeechSynthesisUtterance(texte);
    u.lang = langue;
    u.onend = () => fini();
    u.onerror = () => fini();
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  } catch { fini(); }
}
